import React from 'react';
import { MessageSquare, FileText, RefreshCw, HelpCircle, Settings, RotateCcw } from 'lucide-react';
import KBIcon from './KBIcon';
import { useKB } from './KBContext';
import { libraryHelpUrl } from '../../utils/hostMode';

const KBSidebar = () => {
  const {
    activeTab, setActiveTab, filterOptions, documents,
    isIngesting, handleSyncNew,
    activeCategories, setActiveCategories, toggleCategory,
    activeYears, setActiveYears, toggleYear,
    activeFileTypes, setActiveFileTypes, toggleFileType,
    activeTopics, setActiveTopics, toggleTopic,
    showAllTopics, setShowAllTopics,
    hasActiveFilters, clearFilters,
    fetchDocuments, memberInfo
  } = useKB();

  return (
    <div className="kb-sidebar" data-testid="kb-sidebar">
      <div className="kb-sidebar-header">
        <div className="kb-sidebar-brand">
          <KBIcon size={36} className="kb-brand-icon" />
          <div className="kb-sidebar-brand-text">
            <span className="kb-brand-title">IPMI Document Intelligence</span>
            <span className="kb-brand-sub">AI-Powered Document Intelligence</span>
          </div>
        </div>
      </div>

      <div className="kb-tabs">
        <button className={`kb-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')} data-testid="kb-tab-chat">
          <MessageSquare size={16} /> Chat
        </button>
        <button className={`kb-tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')} data-testid="kb-tab-documents">
          <FileText size={16} /> Documents
        </button>
      </div>

      <div className="kb-sidebar-stats">
        <div className="kb-stat-row">
          <span>{filterOptions.total_documents || documents.length} Documents</span>
          <div className="kb-stat-actions">
            {/* Sync new documents — admin action (R2 ingest). Staff/admin only. */}
            {!memberInfo && (
              <button className="kb-ingest-btn-small" onClick={handleSyncNew} disabled={isIngesting}
                title="Sync new documents from R2" data-testid="kb-sync-btn">
                <RefreshCw size={14} className={isIngesting ? 'kb-spin' : ''} />
              </button>
            )}
            <button className="kb-ingest-btn-small" onClick={() => window.open(libraryHelpUrl(), '_blank')}
              data-testid="kb-guide-btn" title="Quick Guide">
              <HelpCircle size={14} />
            </button>
            {/* Settings & Admin — staff/admin only. Hidden for signed-in members. */}
            {!memberInfo && (
              <button className="kb-ingest-btn-small" onClick={() => setActiveTab(activeTab === 'admin' ? 'chat' : 'admin')}
                data-testid="kb-gear-btn" title="Settings & Admin">
                <Settings size={14} className={activeTab === 'admin' ? 'kb-gear-active' : ''} />
              </button>
            )}
          </div>
        </div>
        {filterOptions.categories?.length > 0 && (
          <div className="kb-stat-row sub">
            <span>{filterOptions.categories.length} categories</span>
            <span>{filterOptions.years?.length || 0} years</span>
          </div>
        )}
      </div>

      {/* Only show filters in Documents tab */}
      {activeTab === 'documents' && (
        <div className="kb-sidebar-filters">
          {hasActiveFilters && (
            <button className="kb-filter-reset-all" onClick={clearFilters} data-testid="kb-filter-reset-all">
              <RotateCcw size={12} /> Reset all filters
            </button>
          )}
          <div className="kb-sidebar-filter-label">Categories <span style={{fontSize:'0.7rem',opacity:0.6}}>(multi-select)</span></div>
          <button className={`kb-filter-chip ${activeCategories.length === 0 ? 'active' : ''}`}
            onClick={() => setActiveCategories([])}>All</button>
          {filterOptions.categories?.map(cat => (
            <button key={cat} className={`kb-filter-chip ${activeCategories.includes(cat) ? 'active' : ''}`}
              onClick={() => toggleCategory(cat)}
              data-testid={`kb-filter-cat-${cat}`}>{cat}</button>
          ))}

          {filterOptions.years?.length > 0 && (
            <>
              <div className="kb-sidebar-filter-label">Years <span style={{fontSize:'0.7rem',opacity:0.6}}>(multi-select)</span></div>
              <button className={`kb-filter-chip ${activeYears.length === 0 ? 'active' : ''}`}
                onClick={() => setActiveYears([])}>All</button>
              {filterOptions.years?.map(yr => (
                <button key={yr} className={`kb-filter-chip ${activeYears.includes(String(yr)) ? 'active' : ''}`}
                  onClick={() => toggleYear(yr)}
                  data-testid={`kb-filter-year-${yr}`}>{yr}</button>
              ))}
            </>
          )}

          {filterOptions.file_types?.length > 0 && (
            <>
              <div className="kb-sidebar-filter-label">File Types <span style={{fontSize:'0.7rem',opacity:0.6}}>(multi-select)</span></div>
              <button className={`kb-filter-chip ${activeFileTypes.length === 0 ? 'active' : ''}`}
                onClick={() => setActiveFileTypes([])}>All</button>
              {filterOptions.file_types?.map(ft => (
                <button key={ft} className={`kb-filter-chip ${activeFileTypes.includes(ft) ? 'active' : ''}`}
                  onClick={() => toggleFileType(ft)}
                  data-testid={`kb-filter-type-${ft}`}>{ft.toUpperCase()}</button>
              ))}
            </>
          )}

          {filterOptions.key_topics?.length > 0 && (
            <>
              <div className="kb-sidebar-filter-label">Key Topics <span style={{fontSize:'0.7rem',opacity:0.6}}>(multi-select)</span></div>
              <button className={`kb-filter-chip ${activeTopics.length === 0 ? 'active' : ''}`}
                onClick={() => { setActiveTopics([]); fetchDocuments({ key_topics: [] }); }}>All</button>
              {(showAllTopics ? filterOptions.key_topics : filterOptions.key_topics.slice(0, 10)).map(t => (
                <button key={t.topic} className={`kb-filter-chip ${activeTopics.includes(t.topic) ? 'active' : ''}`}
                  onClick={() => toggleTopic(t.topic)}
                  data-testid={`kb-filter-topic-${t.topic}`}>
                  {t.topic} <span className="kb-filter-chip-count">{t.count}</span>
                </button>
              ))}
              {filterOptions.key_topics.length > 10 && (
                <button className="kb-filter-show-more" onClick={() => setShowAllTopics(!showAllTopics)}
                  data-testid="kb-topics-show-more">
                  {showAllTopics ? 'Show less' : `+ ${filterOptions.key_topics.length - 10} more`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default KBSidebar;
